import React from 'react';
import _ from 'underscore';

import { Constants, InsertionOrderUtils } from '@bingads-webui-clientcenter/entity-utils';
import IWindow from '@bingads-webui-clientcenter/window-interface';

import { Severity, EstimateType, ActionType, InsertionOrderTextKey, IOAuthzAction } from '../constants';
import { formatCurrency, formatDate } from './format-utils';
import { MonitoringCardButton } from '../controls';

const {
  RecommendationType,
  BudgetRecommendationItemType,
  BudgetEventType,
  IOFilter,
  IOEntryPointAlias,
  SecurityAction,
} = Constants;
const { isEndlessIOEndDate, isUnlimitedIOBudget } = InsertionOrderUtils;

const calcDelta = (a, b) => {
  if (!_.isNumber(a) || !_.isNumber(b)) {
    return null;
  }

  return a - b;
};

const calcDeltaItem = (itemA, itemB) => {
  if (!itemA || !itemB) {
    return null;
  }

  return {
    EstimatedConversionsIncrease: calcDelta(itemA.EstimatedConversions, itemB.EstimatedConversions),
    EstimatedClicksIncrease: calcDelta(itemA.EstimatedClicks, itemB.EstimatedClicks),
    EstimatedImpressionsIncrease: calcDelta(itemA.EstimatedImpressions, itemB.EstimatedImpressions),
    EstimatedSpendIncrease: calcDelta(itemA.EstimatedSpend, itemB.EstimatedSpend),
  };
};

export const populateEstimatedIncrease = (campaignBudgetRecommendation) => {
  if (!campaignBudgetRecommendation) {
    return null;
  }

  const budgetRecommendationItems = campaignBudgetRecommendation.BudgetRecommendationItems;
  const current = _.findWhere(budgetRecommendationItems, { Comment: BudgetRecommendationItemType.Current });

  return {
    ...campaignBudgetRecommendation,
    BudgetRecommendationItems: budgetRecommendationItems &&
      _.map(budgetRecommendationItems, item => ({
        ...item,
        ...calcDeltaItem(item, current),
      })),
  };
};

export const getDeltaEstimates = (campaignBudgetRecommendation) => {
  const suggested = _.findWhere(
    _.get(campaignBudgetRecommendation, 'BudgetRecommendationItems'),
    { Comment: BudgetRecommendationItemType.Suggested }
  );

  if (!suggested) {
    return null;
  }

  const {
    EstimatedConversionsIncrease,
    EstimatedClicksIncrease,
    EstimatedImpressionsIncrease,
    EstimatedSpendIncrease,
  } = suggested;

  return _.compact([
    EstimatedConversionsIncrease && { type: EstimateType.Conversions, amount: EstimatedConversionsIncrease },
    EstimatedClicksIncrease && { type: EstimateType.Clicks, amount: EstimatedClicksIncrease },
    EstimatedImpressionsIncrease && { type: EstimateType.Impressions, amount: EstimatedImpressionsIncrease },
    EstimatedSpendIncrease && { type: EstimateType.Spend, amount: EstimatedSpendIncrease },
  ]);
};

export const formatBudgetEventTypes = ({
  i18n,
  budgetEventTypes,
  campaignBudgetRecommendation,
  openBudgetUpdatePanel,
  navigateBySiteMapId,
  authzMap,
  activity,
}) => {
  const deltaEstimates = getDeltaEstimates(campaignBudgetRecommendation);
  const isExperimentCampaign = _.get(campaignBudgetRecommendation, 'IsExperimentCampaign');

  const formatBudgetEventType = (type) => {
    const handleClick = ({ api, action }) => {
      activity && activity.trace(`BudgetEventType: ${type}`, api);
      action();
    };

    switch (type) {
      case BudgetEventType.CampaignOutOfBudget:
        return {
          severity: Severity.Error,
          message: i18n.getString(_TL_('Your campaign is out of budget. Please increase your budget to ensure your ads keep serving.')),
          deltaEstimates,
          actions: isExperimentCampaign || !_.get(authzMap, SecurityAction.CampaignManagementFullControl)
            ? null
            : <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.EditBudget}
                onClick={() => handleClick({
                  api: 'openBudgetUpdatePanel',
                  action: openBudgetUpdatePanel,
                })}
            />,
        };
      case BudgetEventType.CampaignLimitedByBudget:
        return {
          severity: Severity.Warning,
          message: i18n.getString(_TL_('Your campaign will be limited by budget. Please increase your budget to avoid missing potential traffic.')),
          deltaEstimates,
          actions: isExperimentCampaign || !_.get(authzMap, SecurityAction.CampaignManagementFullControl)
            ? null
            : <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.EditBudget}
                onClick={() => handleClick({
                  api: 'openBudgetUpdatePanel',
                  action: openBudgetUpdatePanel,
                })}
            />,
        };
      case BudgetEventType.PrepayAccountBalanceLow:
        return {
          severity: Severity.Warning,
          message: i18n.getString(_TL_('Your prepay balance is under 15% of your last recharge amount. Please add funds to ensure your ads keep serving.')),
          actions: _.get(authzMap, SecurityAction.ForceBilling)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.AddFunds}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('addFunds', { extraSearch: { returnurl: IWindow.location.href } }),
                })}
            />
            : null,
        };
      case BudgetEventType.PrepayAccountBalanceZero:
        return {
          severity: Severity.Error,
          message: i18n.getString(_TL_('Your prepay balance is depleted. Please add funds to ensure your ads keep serving.')),
          actions: _.get(authzMap, SecurityAction.ForceBilling)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.AddFunds}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('addFunds', { extraSearch: { returnurl: IWindow.location.href } }),
                })}
            />
            : null,
        };
      case BudgetEventType.InsertionOrderBalanceDepleted:
      case BudgetEventType.InsertionOrderExpired:
        return {
          severity: Severity.Error,
          message: i18n.getString(_TL_('Your insertion order is paused due to expiration or depletion. Please fix your existing insertion order or create a new one.')),
          actions: _.get(authzMap, IOAuthzAction.isIOAddAuthorized)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.FixIt}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('insertionOrder', { extraSearch: { iofilter: IOFilter.AccountPaused, entrypoint: IOEntryPointAlias.BudgetCenter } }),
                })}
            />
            : null,
        };
      case BudgetEventType.InsertionOrderExhaustingUrgent:
        return {
          severity: Severity.Warning,
          message: i18n.getString(_TL_('Your insertion order is going to exhaust in 7 days. To keep your ads running, please increase the budget or create a new one.')),
          actions: _.get(authzMap, IOAuthzAction.isIOAddAuthorized)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.FixIt}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('insertionOrder', { extraSearch: { iofilter: IOFilter.Exhausting, entrypoint: IOEntryPointAlias.BudgetCenter } }),
                })}
            />
            : null,
        };
      case BudgetEventType.InsertionOrderExpiringUrgent:
        return {
          severity: Severity.Warning,
          message: i18n.getString(_TL_('Your insertion order will expire in 7 days. To ensure your ads keep serving, please extend the expiration date of your current insertion order or create a new one.')),
          actions: _.get(authzMap, IOAuthzAction.isIOAddAuthorized)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.FixIt}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('insertionOrder', { extraSearch: { iofilter: IOFilter.Expiring, entrypoint: IOEntryPointAlias.BudgetCenter } }),
                })}
            />
            : null,
        };
      case BudgetEventType.AccountHold:
        return {
          severity: Severity.Error,
          message: i18n.getString(_TL_('Your payment method is declined. Please try again.')),
          actions: _.get(authzMap, SecurityAction.ForceBilling)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.FixIt}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('payNow', { extraSearch: { returnurl: IWindow.location.href } }),
                })}
            />
            : null,
        };
      default:
        return null;
    }
  };

  const budgetEventTypesDeduped = _.contains(budgetEventTypes, BudgetEventType.InsertionOrderBalanceDepleted) && _.contains(budgetEventTypes, BudgetEventType.InsertionOrderExpired)
    ? _.without(budgetEventTypes, BudgetEventType.InsertionOrderExpired)
    : budgetEventTypes;

  return _.compact(_.map(budgetEventTypesDeduped, (budgetEventType) => {
    const formatted = formatBudgetEventType(budgetEventType);
    return formatted && ({
      eventType: budgetEventType,
      budgetEventType,
      ...formatted,
    });
  }));
};

export const formatAccountRecommendations = ({
  i18n,
  currencyCode,
  accountRecommendations,
  navigateBySiteMapId,
  authzMap,
  activity,
}) => {
  const formatAccountRecommendation = ({
    RecommendationType: type,
    FromDate,
    ThruDate,
    SpendCapAmount,
    SeriesStartDate,
  }) => {
    const handleClick = ({ api, action }) => {
      activity && activity.trace(`RecommendationType: ${type}`, api);
      action();
    };
    switch (type) {
      case RecommendationType.InsertionOrderCreation: {
        const fromDate = formatDate({ i18n, dateString: FromDate });
        const thruDate = isEndlessIOEndDate(ThruDate)
          ? i18n.getString(InsertionOrderTextKey.NoEndDateLowerCase)
          : formatDate({ i18n, dateString: ThruDate });
        const spendCapAmount = isUnlimitedIOBudget(SpendCapAmount)
          ? i18n.getString(InsertionOrderTextKey.UnlimitedLowerCase)
          : formatCurrency({ i18n, value: SpendCapAmount, currencyCode });
        return {
          severity: Severity.Recommendation,
          message: i18n.getString(
            _TL_('Create a new insertion order from {{fromDate}} to {{thruDate}} with {{spendCapAmount}} budget.'),
            { fromDate, thruDate, spendCapAmount }
          ),
          hint: i18n.getString(_TL_('We recommend this based on the current trends of your insertion order frequency and spend rate.')),
          actions: _.get(authzMap, IOAuthzAction.isIOAddAuthorized)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.Apply}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('insertionOrder', { extraSearch: { entrypoint: IOEntryPointAlias.BudgetCenter } }),
                })}
            />
            : null,
        };
      }
      case RecommendationType.InsertionOrderUpdate: {
        const spendCapAmount = isUnlimitedIOBudget(SpendCapAmount)
          ? i18n.getString(InsertionOrderTextKey.UnlimitedLowerCase)
          : formatCurrency({ i18n, value: SpendCapAmount, currencyCode });
        return {
          severity: Severity.Recommendation,
          message: i18n.getString(
            _TL_('Increase your insertion order budget to {{spendCapAmount}} to get more traffic.'),
            { spendCapAmount }
          ),
          actions: _.get(authzMap, IOAuthzAction.isIOUpdateAuthorized)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.Apply}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('insertionOrder', { extraSearch: { entrypoint: IOEntryPointAlias.BudgetCenter } }),
                })}
            />
            : null,
        };
      }
      case RecommendationType.RecurringInsertionOrderAdd: {
        const seriesStartDate = formatDate({ i18n, dateString: SeriesStartDate });
        return {
          severity: Severity.Recommendation,
          message: i18n.getString(
            _TL_('Create recurring insertion orders from {{seriesStartDate}}.'),
            { seriesStartDate }
          ),
          hint: i18n.getString(_TL_('We recommend this based on the current trends of your insertion order frequency and spend rate.')),
          actions: _.get(authzMap, IOAuthzAction.isIOAddAuthorized)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.Apply}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('insertionOrder', { extraSearch: { entrypoint: IOEntryPointAlias.BudgetCenter } }),
                })}
            />
            : null,
        };
      }
      case RecommendationType.AutoRecharge:
        return {
          severity: Severity.Recommendation,
          message: i18n.getString(_TL_('Turn on auto-recharge for your prepaid account to ensure your ads keep serving.')),
          hint: i18n.getString(_TL_('Auto-recharge can help your prepay account avoid delays in delivering ads.')),
          actions: _.get(authzMap, SecurityAction.AccountUpdate)
            ? <MonitoringCardButton
                i18n={i18n}
                actionType={ActionType.Enable}
                onClick={() => handleClick({
                  api: 'navigate',
                  action: () => navigateBySiteMapId('billingSettings', { extraSearch: { returnurl: IWindow.location.href } }),
                })}
            />
            : null,
        };
      default:
        return null;
    }
  };

  return _.compact(_.map(accountRecommendations, (accountRecommendation) => {
    const formatted = formatAccountRecommendation(accountRecommendation);
    return formatted && ({
      eventType: accountRecommendation.RecommendationType,
      ...accountRecommendation,
      ...formatted,
    });
  }));
};

export const getMonitoringCardConfig = ({
  i18n,
  authzMap,
  currencyCode,
  activity,
  accountRecommendations,
  budgetEventTypes,
  campaignBudgetRecommendation,
  openBudgetUpdatePanel,
  navigateBySiteMapId,
}) => {
  const budgetEventAlerts = formatBudgetEventTypes({
    i18n, budgetEventTypes, campaignBudgetRecommendation, openBudgetUpdatePanel, navigateBySiteMapId, authzMap, activity,
  });
  const accountRecommendationAlerts = formatAccountRecommendations({
    i18n, currencyCode, accountRecommendations, navigateBySiteMapId, authzMap, activity,
  });
  return _.sortBy([...budgetEventAlerts, ...accountRecommendationAlerts], 'severity');
};
